import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { PropsOf } from '@headlessui/react/dist/types';
import { twMerge } from 'tailwind-merge';
import { TbTrash, TbPercentage, TbCategory, TbPig } from 'react-icons/tb';
import { FaReceipt } from 'react-icons/fa';
import {
  BankTransaction,
  BankTransactionParticipation,
  BankTransactionWithParticipation,
  TransactionType,
  CreateBankTransactionModel,
  BankMutation,
} from '../../../types/bankAccount';
import { RootState } from '../../../redux/reducers';
import { ConnectionType } from '../../../redux/slices/connectionsSlice';
import Avatar from '../../components/misc/Avatar';
import PageHeader from '../../components/misc/PageHeader';
import SearchBar from '../../components/forms/SearchBar';
import EmptyScreenView from '../../components/misc/EmptyScreenView';
import {
  BaseTable,
  BaseTableBody,
  BaseTableDataRow,
  BaseTableHeader,
  BaseTableHeaderRow,
  BaseTableSpacerRow,
} from '../../components/tables/BaseTable';
import { formatDate } from '../../../utils/stringUtils';
import * as bankAccountService from '../../../services/bankAccountService';
import TransactionModal from '../../components/modals/TransactionModal';
import DeclarationModal from '../../components/modals/DeclarationModal';
import DeleteSplitTransactionWarningModal from '../../components/modals/DeleteSplitTransactionWarningModal';
import useSelectedBusiness from '../../../hooks/business/useSelectedBusiness';
import { formatEuroCents } from '../../../utils/bankAccountUtil';
import { ActionMenu, ActionMenuButton } from '../../components/menus/ActionMenu';
import connectionService from '../../../services/connectionService';
import OverviewCard from '../../components/cards/OverviewCard';
import TransactionProcessor from '../../components/financial/TransactionProcessor';

interface TransactionsPageProps {
  hideHeader?: boolean;
}

export default function TransactionsPage({
  hideHeader = false,
}: TransactionsPageProps): JSX.Element | null {
  const { t, i18n } = useTranslation('', { keyPrefix: 'page.financial.transaction' });
  const { t: tEnum } = useTranslation('', { keyPrefix: 'page.financial.enums' });
  const { t: tEmpty } = useTranslation('', { keyPrefix: 'page.financial.transaction.emptyState' });
  const [transactions, setTransactions] = useState<BankTransactionWithParticipation[] | null>();
  const business = useSelectedBusiness();
  const [search, setSearch] = useState<string>('');
  const [splitModalTransaction, setSplitModalTransaction] = useState<BankTransaction | null>(null);
  const [splitModalOpen, setSplitModalOpen] = useState<boolean>(false);
  const [deleteWarningModalOpen, setDeleteWarningModalOpen] = useState<boolean>(false);
  const [transactionToDelete, setTransactionToDelete] =
    useState<BankTransactionWithParticipation | null>(null);
  const [mutations, setMutations] = useState<BankMutation[] | null>(null);

  // Calculate percentage of transactions processed by Vera (-42)
  const getProcessedByVeraPercentage = (): number => {
    if (!transactions || transactions.length === 0) return 0;

    const processedByVera = transactions.filter((transaction) => {
      // Check if transaction is processed by Vera (userId === -42)
      if (transaction.processedByConnection?.user?.userId === -42) {
        return true;
      }

      // For internal transactions, check if processedByConnection exists and userId is -42
      if (
        transaction.transactionType === 'INTERNAL_BANK_TRANSFER' &&
        transaction.processedByConnection?.user?.userId === -42
      ) {
        return true;
      }

      return false;
    });

    return Math.round((processedByVera.length / transactions.length) * 100);
  };

  useEffect(() => {
    bankAccountService.getAllTransactions().then(setTransactions);
    connectionService.getConnections();
  }, [business]);

  useEffect(() => {
    if (business) {
      // Fetch mutations to check for split transactions
      bankAccountService.getAllBankAccounts().then((accounts) => {
        if (accounts.length > 0) {
          bankAccountService.getAllMutationsForBank(accounts[0].accountId).then(setMutations);
        }
      });
    }
  }, [business]);

  const openTransactionModal = (transaction: BankTransactionWithParticipation) => {
    setSplitModalTransaction(transaction);
    setTimeout(() => setSplitModalOpen(true), 10);
  };

  const isTransactionFromSplitMutation = (
    transaction: BankTransactionWithParticipation,
  ): boolean => {
    if (!transaction.mutationId || !mutations) return false;

    const mutation = mutations.find((m) => m.mutationId === transaction.mutationId);
    return mutation ? mutation.bankTransactionCount > 1 : false;
  };

  const handleDeleteTransaction = (transaction: BankTransactionWithParticipation) => {
    if (isTransactionFromSplitMutation(transaction)) {
      setTransactionToDelete(transaction);
      setDeleteWarningModalOpen(true);
    } else {
      // Direct delete for non-split transactions
      performDeleteTransaction(transaction);
    }
  };

  const performDeleteTransaction = (transaction: BankTransactionWithParticipation) => {
    bankAccountService
      .deleteTransaction(transaction.transactionId)
      .then(() => {
        setTransactions((prev) =>
          prev ? prev.filter((tns) => tns.transactionId !== transaction.transactionId) : prev,
        );
        toast.success(t('success.deleted'));
      })
      .catch((error) => {
        toast.error(t('error.deletionFailed', { error: error.message }));
      });
  };

  const performDeleteAllTransactionsForMutation = (
    transaction: BankTransactionWithParticipation,
  ) => {
    if (!transaction.mutationId || !transactions) return;

    // Find all transactions with the same mutationId
    const transactionsToDelete = transactions.filter(
      (tns) => tns.mutationId === transaction.mutationId,
    );

    // Delete all transactions with the same mutationId
    const deletePromises = transactionsToDelete.map((tns) =>
      bankAccountService.deleteTransaction(tns.transactionId),
    );

    Promise.all(deletePromises)
      .then(() => {
        setTransactions((prev) =>
          prev ? prev.filter((tns) => tns.mutationId !== transaction.mutationId) : prev,
        );
        toast.success(t('success.deleted'));
      })
      .catch((error) => {
        toast.error(t('error.deletionFailed', { error: error.message }));
      });
  };

  if (!business) return null;

  return (
    <>
      {!hideHeader && (
        <PageHeader title={t('title')} subtitle={t('subtitle')} primaryButton={undefined} />
      )}

      {/* Dashboard Cards */}
      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <OverviewCard
          icon={TbPercentage}
          title={t('overview.processedByVera')}
          seeMoreText=""
          seeMoreLinkProps={{ to: '/financial/administration' }}>
          <p className="text-4xl">{getProcessedByVeraPercentage()}%</p>
        </OverviewCard>
        <OverviewCard
          icon={FaReceipt}
          title={t('overview.declarations')}
          seeMoreText={t('overview.seeMore')}
          seeMoreLinkProps={{ to: '/financial/declarations' }}>
          <p className="text-4xl">
            {transactions?.filter((transaction) => transaction.payerId).length || 0}
          </p>
        </OverviewCard>
        <OverviewCard
          icon={TbCategory}
          title={t('overview.categories')}
          seeMoreText={t('overview.seeMore')}
          seeMoreLinkProps={{ to: '/financial/categories' }}>
          <p className="text-2xl text-gray-500">{t('overview.comingSoon')}</p>
        </OverviewCard>
        <OverviewCard
          icon={TbPig}
          title={t('overview.pots')}
          seeMoreText={t('overview.seeMore')}
          seeMoreLinkProps={{ to: '/financial/pots' }}>
          <p className="text-2xl text-gray-500">{t('overview.comingSoon')}</p>
        </OverviewCard>
      </div>

      {/* Search Bar */}
      {transactions && transactions.length > 0 && (
        <div className="mb-4 flex items-center justify-between">
          <SearchBar className="max-w-[380px]" search={search} setSearch={setSearch} />
        </div>
      )}

      <div className="flex flex-1 flex-col gap-5 pb-4">
        {!transactions ? (
          <EmptyScreenView subtitle="" />
        ) : transactions.length === 0 ? (
          <EmptyScreenView title={tEmpty('title')} subtitle={tEmpty('subtitle')} />
        ) : (
          <>
            <div className="flex flex-col gap-8">
              <div className="relative w-full overflow-x-auto">
                <BaseTable>
                  <BaseTableHeader>
                    <BaseTableHeaderRow>
                      <HeaderCell>{t('table.date')}</HeaderCell>
                      <HeaderCell>{t('table.description')}</HeaderCell>
                      <HeaderCell>{t('table.amount')}</HeaderCell>
                      <HeaderCell>{t('table.type')}</HeaderCell>
                      <HeaderCell>{t('table.debit')}</HeaderCell>
                      <HeaderCell>{t('table.credit')}</HeaderCell>
                      <HeaderCell>{t('table.extraInfo')}</HeaderCell>
                      <HeaderCell>{t('table.processedBy')}</HeaderCell>
                      <HeaderCell>{t('table.actions')}</HeaderCell>
                    </BaseTableHeaderRow>
                  </BaseTableHeader>
                  <BaseTableBody>
                    <BaseTableSpacerRow />
                    {transactions
                      .flatMap<BankTransactionWithParticipation & { declarationStep?: number }>(
                        (transaction) => {
                          if (transaction.payerId) {
                            // This is a declaration transaction, so we don't show it in the list
                            return [
                              { ...transaction, declarationStep: 0 },
                              { ...transaction, declarationStep: 1 },
                            ];
                          }

                          return [transaction];
                        },
                      )
                      .map(({ declarationStep, ...transaction }, i) => (
                        <BaseTableDataRow
                          className="group cursor-pointer transition-all hover:bg-secondary-200"
                          key={`${transaction.transactionId}_tr_${i}`}>
                          <DataCell onClick={() => openTransactionModal(transaction)}>
                            {formatDate(new Date(transaction.date))}
                          </DataCell>
                          <DataCell onClick={() => openTransactionModal(transaction)}>
                            <p className="text-sm">
                              {transaction.description.slice(0, 50)}
                              {transaction.description.length >= 50 && '...'}
                            </p>
                          </DataCell>
                          <DataCell onClick={() => openTransactionModal(transaction)}>
                            {formatEuroCents(transaction.amount, i18n.language)}
                          </DataCell>
                          <DataCell onClick={() => openTransactionModal(transaction)}>
                            {declarationStep === 0
                              ? t('table.declaration')
                              : tEnum(`TransactionType.${transaction.transactionType}`)}
                          </DataCell>
                          <DataCell onClick={() => openTransactionModal(transaction)}>
                            <PayerInfo
                              declarationStep={declarationStep}
                              transaction={transaction}
                            />
                          </DataCell>
                          <DataCell onClick={() => openTransactionModal(transaction)}>
                            <PayeeInfo
                              declarationStep={declarationStep}
                              transaction={transaction}
                            />
                          </DataCell>
                          <DataCell onClick={() => openTransactionModal(transaction)}>
                            <ExtraInfo transaction={transaction} />
                          </DataCell>
                          <DataCell onClick={() => openTransactionModal(transaction)}>
                            <TransactionProcessor transaction={transaction} />
                          </DataCell>
                          <DataCell>
                            <ActionMenu>
                              <ActionMenuButton
                                icon={TbTrash}
                                text={t('table.delete')}
                                className="text-danger"
                                onClick={() => handleDeleteTransaction(transaction)}
                              />
                            </ActionMenu>
                          </DataCell>
                        </BaseTableDataRow>
                      ))}
                  </BaseTableBody>
                  {/*<BaseTablePagination pagination={pagination} />*/}
                </BaseTable>
              </div>
            </div>
          </>
        )}
        {splitModalTransaction && splitModalTransaction.payerId && (
          <DeclarationModal
            open={splitModalOpen}
            setOpen={(open) => {
              if (open) {
                setSplitModalTransaction(splitModalTransaction);
                setTimeout(() => setSplitModalOpen(true), 10);
              } else {
                setSplitModalOpen(false);
                setTimeout(() => setSplitModalTransaction(null), 200);
              }
            }}
            initialTransaction={splitModalTransaction}
            onSplitSuccess={(transaction) => {
              if (!splitModalTransaction) return;

              bankAccountService
                .updateBankTransaction(splitModalTransaction?.transactionId, transaction)
                .then((updatedTransaction) => {
                  setTransactions((prev) =>
                    prev
                      ? prev.map((tns) =>
                          tns.transactionId === splitModalTransaction.transactionId
                            ? updatedTransaction
                            : tns,
                        )
                      : prev,
                  );
                  toast.success(t('success.updated'));
                  setSplitModalOpen(false);
                  setTimeout(() => setSplitModalTransaction(null), 200);
                });
            }}
          />
        )}
        {splitModalTransaction && !splitModalTransaction.payerId && (
          <TransactionModal
            open={splitModalOpen}
            setOpen={(open) => {
              if (open) {
                setSplitModalTransaction(splitModalTransaction);
                setTimeout(() => setSplitModalOpen(true), 10);
              } else {
                setSplitModalOpen(false);
                setTimeout(() => setSplitModalTransaction(null), 200);
              }
            }}
            initialTransaction={splitModalTransaction}
            onSplitSuccess={(
              transaction: CreateBankTransactionModel | CreateBankTransactionModel[],
            ) => {
              if (!splitModalTransaction) return;

              if (Array.isArray(transaction)) {
                // Handle split transactions - for now, just show a message that this is not supported in edit mode
                toast.error(t('error.splitEditNotSupported'));
                setSplitModalOpen(false);
                setTimeout(() => setSplitModalTransaction(null), 200);
              } else {
                // Handle single transaction update
                bankAccountService
                  .updateBankTransaction(splitModalTransaction?.transactionId, transaction)
                  .then((updatedTransaction) => {
                    setTransactions((prev) =>
                      prev
                        ? prev.map((tns) =>
                            tns.transactionId === splitModalTransaction.transactionId
                              ? updatedTransaction
                              : tns,
                          )
                        : prev,
                    );
                    toast.success(t('success.updated'));
                    setSplitModalOpen(false);
                    setTimeout(() => setSplitModalTransaction(null), 200);
                  });
              }
            }}
          />
        )}

        <DeleteSplitTransactionWarningModal
          open={deleteWarningModalOpen}
          setOpen={setDeleteWarningModalOpen}
          transaction={transactionToDelete}
          onConfirmDeleteAll={() => {
            if (transactionToDelete) {
              performDeleteAllTransactionsForMutation(transactionToDelete);
            }
          }}
        />
      </div>
    </>
  );
}

function AvatarList({ participants }: { participants: BankTransactionParticipation[] }) {
  const connectionsAll = useSelector((state: RootState) => state.connections);
  const connections = connectionsAll.filter((c) => c.type === ConnectionType.B2C_CONNECTION);

  const displayParticipants = participants
    .map((p) => connections.find((connection) => +connection.id === +p.connectionId))
    .filter(Boolean);

  return (
    <div className="flex items-center gap-[-8px]">
      {displayParticipants.length === 1 ? (
        <div className="flex items-center gap-2">
          <Avatar
            className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-900 text-xs font-bold text-secondary-50 shadow-md"
            src={displayParticipants[0]?.picture}
            alias={`${displayParticipants[0]?.fields.FIRST_NAME}+${displayParticipants[0]?.fields.LAST_NAME}`}
          />
          {displayParticipants[0]?.fields.FIRST_NAME} {displayParticipants[0]?.fields.LAST_NAME}
        </div>
      ) : (
        displayParticipants
          .slice(0, 2)
          .map((c, index) => (
            <Avatar
              key={`avatar_transactions_${index}_${c?.id}`}
              className={`relative z-[${10 - index}] flex h-8 w-8 items-center justify-center rounded-full ${index === 0 ? 'bg-secondary-200' : index === 1 ? 'bg-primary-300' : 'bg-primary-900'} text-xs font-bold text-white shadow-md ${index === 0 ? '' : '-ml-2'}`}
              src={c?.picture}
              alias={`${c?.fields.FIRST_NAME}+${c?.fields.LAST_NAME}`}
            />
          ))
      )}
      {participants.length > 2 && (
        <div
          className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-blue-900 text-sm font-bold text-white shadow-md"
          style={{
            marginLeft: '-8px',
            zIndex: 1,
          }}>
          +{participants.length - 2}
        </div>
      )}
    </div>
  );
}

function PayerInfo({
  transaction,
  declarationStep,
}: {
  transaction: BankTransactionWithParticipation;
  declarationStep?: number;
}): JSX.Element | null {
  const connections = useSelector((state: RootState) => state.connections);
  const business = useSelectedBusiness();
  const isDeclaration = !!transaction.payerId;
  const isContribution = transaction.transactionType === TransactionType.CONTRIBUTION;

  if (!business) return null;

  if (isDeclaration) {
    const payer = connections.find((x) => +x.id === transaction.payerId);
    if (!payer || declarationStep === 1) return null;

    const alias = `${payer.fields.FIRST_NAME}+${payer.fields.LAST_NAME}`;

    return (
      <div className="flex items-center gap-2">
        <Avatar className="h-8 w-8 rounded-full" src={payer?.picture} alias={alias} />
        <span>
          {payer.fields.FIRST_NAME} {payer.fields.LAST_NAME}
        </span>
      </div>
    );
  }

  if (isContribution && transaction.contributionPayment) {
    const payer = connections.find((x) => +x.id === transaction.contributionPayment?.connectionId);
    if (!payer) return null;

    const alias = `${payer.fields.FIRST_NAME}+${payer.fields.LAST_NAME}`;

    return (
      <div className="flex items-center gap-2">
        <Avatar className="h-8 w-8 rounded-full" src={payer?.picture} alias={alias} />
        <span>
          {payer.fields.FIRST_NAME} {payer.fields.LAST_NAME}
        </span>
      </div>
    );
  }

  if (
    transaction.transactionType === TransactionType.GROUP_INCOME ||
    transaction.transactionType === TransactionType.PERSONAL_INCOME
  ) {
    return (
      <>
        {!transaction.participations || transaction.participations.length === 0 ? (
          <div className="flex items-center gap-2">
            <Avatar className="h-8 w-8" src={business.picture ?? undefined} alias={business.name} />
            <span>{business.name}</span>
          </div>
        ) : (
          <AvatarList participants={transaction.participations} />
        )}
      </>
    );
  }

  return null;
}

function PayeeInfo({
  transaction,
  declarationStep,
}: {
  transaction: BankTransactionWithParticipation;
  declarationStep?: number;
}): JSX.Element | null {
  const business = useSelectedBusiness();
  const isDeclaration = !!transaction.payerId;

  if (!business) return null;

  if (isDeclaration && declarationStep === 0) {
    return null;
  }

  if (
    transaction.transactionType === TransactionType.GROUP_EXPENSE ||
    transaction.transactionType === TransactionType.PERSONAL_EXPENSE
  ) {
    return (
      <>
        {!transaction.participations || transaction.participations.length === 0 ? (
          <div className="flex items-center gap-2">
            <Avatar className="h-8 w-8" src={business.picture ?? undefined} alias={business.name} />
            <span>{business.name}</span>
          </div>
        ) : (
          <AvatarList participants={transaction.participations} />
        )}
      </>
    );
  }

  return null;
}

function ExtraInfo({ transaction }: { transaction: BankTransactionWithParticipation }) {
  if (transaction.contributionPayment) {
    // TODO: This should be the name of the contribution plan, not the ID
    // return JSON.stringify(transaction.contributionPayment.contributionPlanId);
    return null;
  }

  return null;
}

function HeaderCell({ className, ...props }: PropsOf<'th'>): JSX.Element {
  return <th className={twMerge('py-3 pl-1 text-primary-900', className)} {...props} />;
}

function DataCell({ className, ...props }: PropsOf<'td'>): JSX.Element {
  return (
    <td
      className={twMerge(
        'whitespace-nowrap py-4 pl-2 text-sm font-medium text-primary-900',
        className,
      )}
      {...props}
    />
  );
}

// type CreateContributionModalProps = {
//   open: boolean;
//   setOpen: (open: boolean) => void;
// };
//
// function CreateContributionModal({ open, setOpen }: CreateContributionModalProps): JSX.Element {
//   const { t } = useTranslation('', { keyPrefix: 'page.financial.createContributionModal' });
//   const [tns, setTns] = useState<Record<keyof CreateBankTransactionModel, string>>(
//     {} as Record<keyof CreateBankTransactionModel, string>,
//   );
//
//   const handleChange = (ev: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>) => {
//     const { name, value } = ev.target;
//     setTns((prev) => ({
//       ...prev,
//       [name]: value,
//     }));
//   };
//
//   return (
//     <ModalLayout
//       className="w-full max-w-2xl rounded-md bg-secondary-50 p-6"
//       open={open}
//       setOpen={setOpen}
//       closeButton>
//       <h3 className="pb-2 text-xl font-semibold">{t('title')}</h3>
//       <form className="flex flex-col gap-2">
//         <Labeled label={t('amount')}>
//           <Input name="amount" type="text" value={tns.amount} onChange={handleChange} />
//         </Labeled>
//         <Labeled label={t('description')}>
//           <Input name="description" type="text" value={tns.description} onChange={handleChange} />
//         </Labeled>
//         <Button type="submit" variant="primary-dark" className="self-end">
//           {t('createButton')}
//         </Button>
//       </form>
//     </ModalLayout>
//   );
// }
